import { z } from 'zod';

/**
 * Brazilian plates — both formats remain valid in the field.
 * Product/OCR priority is **Mercosul** (current standard); old plates still circulate.
 *
 *  - Mercosul: ABC1D23 (LLLNLNN) — preferred for training / vision accuracy goals
 *  - Old:      ABC1234 (LLLNNNN) — still accepted everywhere we validate plates
 *
 * Regexes are anchored and case-sensitive on uppercase — apply normalizePlate first.
 */
const OLD_PLATE_REGEX = /^[A-Z]{3}[0-9]{4}$/;
const MERCOSUL_PLATE_REGEX = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;

/**
 * Strip whitespace, dashes and dots, then uppercase.
 * "abc-1d23 " -> "ABC1D23"
 */
export const normalizePlate = (raw: string): string => raw.replace(/[\s.-]/g, '').toUpperCase();

export const isValidPlate = (normalized: string): boolean =>
  OLD_PLATE_REGEX.test(normalized) || MERCOSUL_PLATE_REGEX.test(normalized);

export type PlateFormat = 'old' | 'mercosul';

/** Prefer mercosul when both could match (they never do with these regexes). */
export const detectPlateFormat = (normalized: string): PlateFormat | null => {
  if (MERCOSUL_PLATE_REGEX.test(normalized)) return 'mercosul';
  if (OLD_PLATE_REGEX.test(normalized)) return 'old';
  return null;
};

export const isMercosulPlate = (normalized: string): boolean =>
  MERCOSUL_PLATE_REGEX.test(normalized);

/**
 * Zod schema accepting raw input, normalizing on parse, rejecting unknown formats.
 * Output is always the normalized uppercase string.
 */
export const plateSchema = z
  .string()
  .min(1, 'plate is required')
  .transform(normalizePlate)
  .refine(isValidPlate, 'invalid plate format (expected ABC1234 or ABC1D23)');

export type Plate = z.infer<typeof plateSchema>;
